import { useRouter } from '@uirouter/react';
import { useImmerAtom } from 'jotai-immer';
import { useProviderEditStore } from '../pagehooks/useProviderEditStore';
import { editModelAtom } from '../provider_edit_atoms';
import {
    validateFieldByLength,
    validatePhone,
    validateZipCode,
} from '../../../../core/components/validation/validators';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import utilSvc from '../../../../core/utilSvc';
import validSvc from '../../../../core/validSvc';
import { HttpError422, HttpUpsert200 } from '../../../../../global';
import providerRepo from '../../data/providerRepo';
import { ROUTE_LIST } from '../../provider_const';
import { AdminProviderModel } from '../provider_edit_types';
import { modelVM } from '../provider_edit_view_models';
import { useCallback } from 'react';

type UseProviderEditModelReturn = {
    model: AdminProviderModel;
    setPropText: (prop: string, val: string | number) => void;
    submit: () => boolean;
    waiting: boolean;
    setWait: (wait: boolean) => void;
    cancelEdit: () => void;
};

export const useProviderEditModel = (): UseProviderEditModelReturn => {
    const router = useRouter();
    const { state, waiting, setWait } = useProviderEditStore();
    const [model, setModel] = useImmerAtom(editModelAtom);

    const setPropText = useCallback(
        (prop: string, val: string | number): void => {
            setModel(draft => {
                draft[prop].val = val;
            });
        },
        [setModel],
    );

    // model validate all props
    const validate = useCallback((): boolean => {
        let result = true;
        setModel(draft => {
            const taxonomy = validateFieldByLength({ prop: draft.PrimaryTaxonomy, required: true });
            const practiceName = validateFieldByLength({ prop: draft.PracticeName, maxLength: 50, required: false });
            const address1 = validateFieldByLength({
                prop: draft.Address1,
                minLength: 3,
                maxLength: 50,
                required: true,
            });
            const address2 = validateFieldByLength({
                prop: draft.Address2,
                minLength: 3,
                maxLength: 50,
                required: false,
            });
            const city = validateFieldByLength({ prop: draft.City, minLength: 3, maxLength: 40, required: true });
            const state = validateFieldByLength({ prop: draft.State, required: true });
            const zip = validateZipCode(draft.Zip);
            const fax = validatePhone(draft.Phone);
            const phone = validatePhone(draft.Fax);

            if (!practiceName || !taxonomy || !address1 || !address2 || !city || !state || !zip || !fax || !phone) {
                result = false;
            }
        });
        return result;
    }, [setModel]);

    const submitAsync = useCallback(async (): Promise<void> => {
        // API: CREATE / UPDATE
        setWait(true);

        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { ChangeLog, Usage, ...restModel } = model;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const r = (await providerRepo.upsert(state.mode, model)) as HttpUpsert200;
            // Get and set updated model
            const detail = (await providerRepo.getDetail(model.Id)) as AdminProviderModel;
            const updatedModel = modelVM(detail);
            const withTaxonomy = {
                ...updatedModel,
                PrimaryTaxonomy: model.PrimaryTaxonomy,
            };
            setModel(() => withTaxonomy);
            ngDelegate.ok('Provider Saved');
        } catch (err: any) {
            err as HttpError422;
            if (utilSvc.http.isIgnore(err)) {
                return;
            }

            // handle 422 as model
            if (err.status === 422) {
                setModel(() => {
                    const errMsg = validSvc.http422ToModel(err as HttpError422, model);
                    ngDelegate.ok('Cannot Save', errMsg);
                });
            }
        } finally {
            setWait(false);
        }
    }, [model, setModel, setWait, state.mode]);

    const submit = useCallback((): boolean => {
        if (waiting) {
            return false;
        }

        if (!validate()) {
            ngDelegate.ok('Cannot Save', 'Check properties');
            return false;
        }

        submitAsync();
        return true;
    }, [submitAsync, validate, waiting]);

    const cancelEdit = useCallback((): void => {
        router.stateService.go(ROUTE_LIST);
    }, [router.stateService]);

    return {
        model,
        setPropText,
        submit,
        waiting,
        setWait,
        cancelEdit,
    };
};
